// Vanuatu National Syllabus: Mathematics Senior Secondary Years 11-13
// Based on official syllabus document revised February 2023

export const mathematicsCurriculum = {
  year11: {
    title: "Year 11 Mathematics",
    description: "Foundation mathematical concepts covering algebra, geometry, calculus introduction, and statistics",
    hoursPerYear: 156,
    hoursPerWeek: 4,
    assessment: {
      internalExamination: 60,
      continuousAssessment: 40
    },
    strands: [
      {
        id: "strand1",
        name: "Algebra",
        hours: 40,
        topics: [
          {
            id: "11MAT1.1",
            name: "Expansion",
            description: "Understanding algebraic expressions using expansion, including binomial expressions and laws of indices",
            weeks: "1-3"
          },
          {
            id: "11MAT1.2",
            name: "Factorization",
            description: "Factorizing algebraic expressions by common factors, grouping, difference of two squares, and quadratic expressions",
            weeks: "4-6"
          },
          {
            id: "11MAT1.3",
            name: "Algebraic Fractions",
            description: "Simplification, addition, subtraction, multiplication and division of algebraic fractions",
            weeks: "7-8"
          },
          {
            id: "11MAT1.4",
            name: "Square Roots and Surds",
            description: "Simplifying surds by adding, subtracting, multiplying and dividing",
            weeks: "9-10"
          },
          {
            id: "11MAT1.5",
            name: "Equations and Inequalities",
            description: "Solving linear equations, inequalities, and quadratic equations using factorization and quadratic formula",
            weeks: "11-13"
          }
        ]
      },
      {
        id: "strand2",
        name: "Geometry and Trigonometry",
        hours: 35,
        topics: [
          {
            id: "11MAT2.1",
            name: "Triangle Geometry",
            description: "Trigonometric ratios, sine and cosine rules, Pythagoras theorem for solving triangles",
            weeks: "1-4"
          },
          {
            id: "11MAT2.2",
            name: "Circle Geometry",
            description: "Finding area of sectors and segments using radians and degrees, arc length calculations",
            weeks: "5-7"
          },
          {
            id: "11MAT2.3",
            name: "Trigonometry Graphs",
            description: "Sketching graphs of y = A sin Bx and y = A cos Bx with amplitude, frequency and period",
            weeks: "8-10"
          },
          {
            id: "11MAT2.4",
            name: "Coordinate Geometry",
            description: "Midpoint, distance, gradient, equation of a line, parallel and perpendicular lines",
            weeks: "11-14"
          }
        ]
      },
      {
        id: "strand3",
        name: "Sequences, Series and Calculus",
        hours: 45,
        topics: [
          {
            id: "11MAT3.1",
            name: "Sequences and Series",
            description: "Arithmetic and geometric sequences, finding nth term and sum of terms",
            weeks: "1-4"
          },
          {
            id: "11MAT3.2",
            name: "Functions",
            description: "Understanding functions, domain and range, evaluating function values",
            weeks: "5-6"
          },
          {
            id: "11MAT3.3",
            name: "Graph of Functions",
            description: "Sketching quadratic, rational, logarithmic, exponential, and cubic graphs",
            weeks: "7-10"
          },
          {
            id: "11MAT3.4",
            name: "Nature of Functions",
            description: "Increasing/decreasing functions, turning points, stationary points",
            weeks: "11-12"
          },
          {
            id: "11MAT3.5",
            name: "Differentiation",
            description: "Derivative of axⁿ, first principles, gradient of tangents, stationary points",
            weeks: "13-16"
          }
        ]
      },
      {
        id: "strand4",
        name: "Probability and Statistics",
        hours: 36,
        topics: [
          {
            id: "11MAT4.1",
            name: "Basic Probability",
            description: "Probability of simple events, outcomes, expressing probability in decimals and percentages",
            weeks: "1-3"
          },
          {
            id: "11MAT4.2",
            name: "Theoretical Probability",
            description: "Combining events, conditional probability, tree diagrams",
            weeks: "4-6"
          },
          {
            id: "11MAT4.3",
            name: "Statistics Parameters",
            description: "Mean, median, mode, range, quartiles, standard deviation for ungrouped data",
            weeks: "7-9"
          },
          {
            id: "11MAT4.4",
            name: "Basic Display of Data",
            description: "Frequency tables, pie charts, bar graphs, histograms, time series",
            weeks: "10-12"
          },
          {
            id: "11MAT4.5",
            name: "Analysis of Data",
            description: "Interpreting graphs, cumulative frequency, ogive curves",
            weeks: "13-15"
          }
        ]
      }
    ]
  },
  year12: {
    title: "Year 12 Mathematics",
    description: "Intermediate mathematics with advanced algebra, trigonometry, calculus, and statistical analysis",
    hoursPerYear: 156,
    hoursPerWeek: 5,
    assessment: {
      externalExamination: 60,
      internalAssessment: 40
    },
    strands: [
      {
        id: "strand1",
        name: "Algebra",
        hours: 45,
        topics: [
          {
            id: "12MAT1.1",
            name: "Linear Equations and Inequalities",
            description: "Linear equations, inequalities, graphing, simultaneous equations, intersection of lines",
            weeks: "1-4"
          },
          {
            id: "12MAT1.2",
            name: "Quadratic Equations and Graphs",
            description: "Factorization, quadratic formula, turning points, transformations, roots",
            weeks: "5-8"
          },
          {
            id: "12MAT1.3",
            name: "Cubic Equations and Graphs",
            description: "Remainder theorem, factor theorem, cubic graphs and their properties",
            weeks: "9-11"
          },
          {
            id: "12MAT1.4",
            name: "Rational Algebraic Expressions",
            description: "Operations on rational expressions, asymptotes, graphing rational functions",
            weeks: "12-14"
          },
          {
            id: "12MAT1.5",
            name: "Exponential and Logarithmic Equations",
            description: "Laws of indices and logarithms, solving equations, graphing",
            weeks: "15-17"
          }
        ]
      },
      {
        id: "strand2",
        name: "Geometry and Trigonometry",
        hours: 38,
        topics: [
          {
            id: "12MAT2.1",
            name: "Coordinate and Line Geometry",
            description: "Distance, midpoint, gradient, equations of lines, parallel and perpendicular lines",
            weeks: "1-3"
          },
          {
            id: "12MAT2.2",
            name: "Triangle Geometry and Trig Identities",
            description: "Sine and cosine rules, area of triangles, trigonometric identities",
            weeks: "4-7"
          },
          {
            id: "12MAT2.3",
            name: "Trigonometric Graphs and Transformations",
            description: "Graphing y = A sin(Bx + C), solving trigonometric equations",
            weeks: "8-11"
          },
          {
            id: "12MAT2.4",
            name: "Circle Geometry and Circular Graphs",
            description: "Arc length, sector and segment area, equations of circles, intersections",
            weeks: "12-15"
          }
        ]
      },
      {
        id: "strand3",
        name: "Sequences, Series and Calculus",
        hours: 45,
        topics: [
          {
            id: "12MAT3.1",
            name: "Arithmetic Sequences and Series",
            description: "nth term, sum of terms, word problems involving arithmetic sequences",
            weeks: "1-3"
          },
          {
            id: "12MAT3.2",
            name: "Geometric Sequences and Series",
            description: "nth term, sum to n terms, sum to infinity, sigma notation",
            weeks: "4-6"
          },
          {
            id: "12MAT3.3",
            name: "Differentiation",
            description: "First principles, derivative rules, tangents, normals, curve sketching",
            weeks: "7-11"
          },
          {
            id: "12MAT3.4",
            name: "Integration",
            description: "Integration of polynomials, definite integrals, area under curves, area between curves",
            weeks: "12-16"
          }
        ]
      },
      {
        id: "strand4",
        name: "Probability and Statistics",
        hours: 28,
        topics: [
          {
            id: "12MAT4.1",
            name: "Statistical Data and Sampling",
            description: "Sampling methods, data types, frequency tables, graphical representation",
            weeks: "1-4"
          },
          {
            id: "12MAT4.2",
            name: "Statistical Analysis",
            description: "Mean, median, mode, standard deviation, interpreting trends",
            weeks: "5-7"
          },
          {
            id: "12MAT4.3",
            name: "Probability of Events",
            description: "Sample space, tree diagrams, compound and conditional probability",
            weeks: "8-10"
          },
          {
            id: "12MAT4.4",
            name: "Normal Distribution",
            description: "Normal curves, z-scores, probabilities using normal distribution",
            weeks: "11-14"
          }
        ]
      }
    ]
  },
  year13: {
    title: "Year 13 Mathematics with Calculus",
    description: "Advanced mathematics with calculus covering complex numbers, algebra, vectors, trigonometry, differentiation, and integration (Vanuatu National Syllabus 2026)",
    hoursPerYear: 165,
    hoursPerWeek: 5,
    totalWeeks: 36,
    teachingWeeks: 33,
    assessment: {
      externalExamination: 60,
      internalAssessment: 40,
      internalBreakdown: {
        task1: 10,
        task2: 10,
        task3: 10,
        task4: 10
      }
    },
    strands: [
      {
        id: "strand1",
        name: "Algebra",
        hours: 64,
        topics: [
          {
            id: "13CAL1.1",
            name: "Complex Numbers",
            description: "Define imaginary numbers, simplify powers of i, modulus and argument, rectangular and polar forms, Argand diagrams, conjugates, operations in rectangular and polar form, solving equations, sketching regions, De Moivre's Theorem, roots of complex numbers, proofs for conjugates/modulus/arguments",
            weeks: "1-6"
          },
          {
            id: "13CAL1.2",
            name: "Binomial Theorem",
            description: "Expand and simplify (x + y)^n for n >= 3, find specific terms using binomial theorem, find coefficients and terms independent of x",
            weeks: "7"
          },
          {
            id: "13CAL1.3",
            name: "Mathematical Induction",
            description: "Prove mathematical statements using induction: simple equations where n is not a power, rational equations, and statements where n is a power (laws of indices applied)",
            weeks: "8"
          },
          {
            id: "13CAL1.4",
            name: "Partial Fractions",
            description: "Write partial fraction templates for linear/repeated/quadratic factors, express algebraic fractions as partial fractions with linear and repeated roots, and with non-linear or irreducible quadratic denominators",
            weeks: "9-10"
          }
        ]
      },
      {
        id: "strand2",
        name: "Geometry and Trigonometry",
        hours: 34,
        topics: [
          {
            id: "13CAL2.1",
            name: "Vectors and Geometry",
            description: "Vector addition/subtraction, scalar multiplication, direction vectors, norm/magnitude, unit vectors, normalization, dot/scalar product, angle between vectors, parallel/perpendicular proofs, equation of a line (parametric/symmetric forms), division of line segments internally and externally",
            weeks: "1-3"
          },
          {
            id: "13CAL2.2",
            name: "Trigonometric Skills",
            description: "Reciprocal and inverse trig functions, exact values using special triangles, sketching trig functions, solving simple trig equations, Pythagorean trig identities, double angle formulas, compound angle formulas, sums and products, graphing reciprocal and inverse trig functions",
            weeks: "4-5"
          },
          {
            id: "13CAL2.3",
            name: "Trigonometric Functions to Solve Problems",
            description: "Solve problems with models y = A sin B(x + C) + D and y = A cos B(x + C) + D, form equations for models using amplitude, period, horizontal shift and vertical shift",
            weeks: "6"
          },
          {
            id: "13CAL2.4",
            name: "Proving Trig Identities",
            description: "Prove identities involving reciprocal relationships (direct substitution and algebraic), Pythagorean identities, compound angle formulas, double angle formulas, and sum and product formulas",
            weeks: "7"
          }
        ]
      },
      {
        id: "strand3",
        name: "Differentiation",
        hours: 38,
        topics: [
          {
            id: "13CAL3.1",
            name: "Limits and Continuity",
            description: "Piecewise function features (continuity, limits, differentiability), finding limits algebraically and numerically, limits as x approaches specific values and infinity, identifying graph features (gradients, concavity, turning points, inflection), discontinuities, sketching piecewise and rational graphs, linking graph features with limiting behaviour, continuity at a point",
            weeks: "1-2"
          },
          {
            id: "13CAL3.2",
            name: "Differentiation Skills",
            description: "Differentiating sums, quotients, composite functions (chain rule), parametric functions, second derivatives, finding maxima/minima with proof, points of inflection, first principles for polynomials of degree <= 3",
            weeks: "3-4"
          },
          {
            id: "13CAL3.3",
            name: "Applications of Differentiation I",
            description: "Kinematics: using differentiation for projectile motion, velocity, acceleration. Optimization: maxima/minima problems with single and double differentiation. Related rates: rates of change using chain rule with multiple variables. Implicit and parametric differentiation: tangent/normal equations, differentiating polynomials, trig, logarithmic and power functions",
            weeks: "5-8"
          },
          {
            id: "13CAL3.4",
            name: "Applications of Differentiation II - Graph Sketching",
            description: "Sketch graphs of polynomials degree >= 3 using differentiation to find turning points, inflection points and concavity regions. Sketch derived function from given graph. Demonstrate knowledge of limits, differentiability, discontinuity, gradients, concavity features",
            weeks: "9"
          }
        ]
      },
      {
        id: "strand4",
        name: "Integration",
        hours: 27,
        topics: [
          {
            id: "13CAL4.1",
            name: "Integration Skills",
            description: "Integrate ax^n, polynomials, exponentials (ax + b form), trigonometric functions including products, definite integrals, area under curves, rational functions, u-substitution Type I (derivative factor) and Type II (expressing x in terms of u)",
            weeks: "1-3"
          },
          {
            id: "13CAL4.2",
            name: "Integration Techniques",
            description: "Areas for odd/even/periodic functions, numerical methods for areas under and between curves. Kinematics integration: anti-differentiation for velocity/distance, solving word problems with one and two levels of integration, area of kinematic equations",
            weeks: "4-5"
          },
          {
            id: "13CAL4.3",
            name: "Volume of Solids of Revolution",
            description: "Integration for volumes of revolution of simple functions (y = ax^n) around axes parallel to x or y axis, complex functions (exponential, trig), and area between two functions (linear, trig, quadratic, exponential, logarithmic, hyperbola)",
            weeks: "6"
          },
          {
            id: "13CAL4.4",
            name: "Differential Equations",
            description: "Form differential equations with rate of change proportional to variable. Solve first and second order separable differential equations. Functions include polynomials, e^x, ln(x), x^n, trig functions. Solve both simple and complex differential equations with rate of change directly or inversely proportional",
            weeks: "7-8"
          }
        ]
      }
    ]
  }
};

export const mathematicsSchemeOfWork = {
  year11: {
    term1: [
      { week: 1, strand: "Algebra", topic: "Algebraic Terms and Expressions", activities: "Define terms, identify like terms, basic operations" },
      { week: 2, strand: "Algebra", topic: "Laws of Indices", activities: "Apply index laws, simplify expressions" },
      { week: 3, strand: "Algebra", topic: "Binomial Expansion", activities: "Perfect squares, difference of two squares" },
      { week: 4, strand: "Algebra", topic: "Factorization by Common Factor", activities: "Factorizing by grouping in pairs" },
      { week: 5, strand: "Algebra", topic: "Factorizing Quadratics", activities: "Quadratic expressions where a=1" },
      { week: 6, strand: "Algebra", topic: "Algebraic Fractions", activities: "Simplifying and multiplying fractions" },
      { week: 7, strand: "Algebra", topic: "Adding and Subtracting Fractions", activities: "Common denominators, simplification" },
      { week: 8, strand: "Algebra", topic: "Surds Introduction", activities: "Define surds, simplify square roots" },
      { week: 9, strand: "Algebra", topic: "Operations with Surds", activities: "Add, subtract, multiply surds" },
      { week: 10, strand: "Algebra", topic: "Linear Equations", activities: "Solving, rearranging formulas" }
    ],
    term2: [
      { week: 1, strand: "Geometry", topic: "Trigonometric Ratios", activities: "Sin, cos, tan for right triangles" },
      { week: 2, strand: "Geometry", topic: "Pythagoras Theorem", activities: "Finding unknown sides" },
      { week: 3, strand: "Geometry", topic: "Sine Rule", activities: "Finding sides and angles" },
      { week: 4, strand: "Geometry", topic: "Cosine Rule", activities: "Non-right triangle calculations" },
      { week: 5, strand: "Geometry", topic: "Radian Measure", activities: "Converting degrees to radians" },
      { week: 6, strand: "Geometry", topic: "Arc Length and Sector Area", activities: "Circle geometry applications" },
      { week: 7, strand: "Geometry", topic: "Trig Graphs Introduction", activities: "Sketching y = sin x, y = cos x" },
      { week: 8, strand: "Geometry", topic: "Amplitude and Period", activities: "Transformations of trig graphs" },
      { week: 9, strand: "Geometry", topic: "Coordinate Geometry Basics", activities: "Distance and midpoint formulas" },
      { week: 10, strand: "Geometry", topic: "Gradient and Equations of Lines", activities: "y = mx + c form" }
    ],
    term3: [
      { week: 1, strand: "Calculus", topic: "Number Sequences", activities: "Identify arithmetic sequences" },
      { week: 2, strand: "Calculus", topic: "Arithmetic Series", activities: "nth term and sum formulas" },
      { week: 3, strand: "Calculus", topic: "Geometric Sequences", activities: "Common ratio, nth term" },
      { week: 4, strand: "Calculus", topic: "Functions and Graphs", activities: "Domain, range, evaluating functions" },
      { week: 5, strand: "Calculus", topic: "Introduction to Differentiation", activities: "Gradient as rate of change" },
      { week: 6, strand: "Calculus", topic: "Derivative Rules", activities: "Differentiating polynomials" },
      { week: 7, strand: "Statistics", topic: "Basic Probability", activities: "Events, outcomes, calculations" },
      { week: 8, strand: "Statistics", topic: "Tree Diagrams", activities: "Conditional probability" },
      { week: 9, strand: "Statistics", topic: "Measures of Central Tendency", activities: "Mean, median, mode" },
      { week: 10, strand: "Statistics", topic: "Data Display", activities: "Frequency tables, graphs" }
    ]
  },
  year12: {
    term1: [
      { week: 1, strand: "Algebra", topic: "Linear Equations Review", activities: "Solving and graphing linear equations" },
      { week: 2, strand: "Algebra", topic: "Linear Inequalities", activities: "Graphing inequalities, solution sets" },
      { week: 3, strand: "Algebra", topic: "Simultaneous Equations", activities: "Elimination and substitution methods" },
      { week: 4, strand: "Algebra", topic: "Quadratic Equations", activities: "Factorization and quadratic formula" },
      { week: 5, strand: "Algebra", topic: "Quadratic Graphs", activities: "Turning points, line of symmetry" },
      { week: 6, strand: "Algebra", topic: "Quadratic Transformations", activities: "Shifts along x and y axes" },
      { week: 7, strand: "Algebra", topic: "Cubic Functions", activities: "Remainder theorem, factorization" },
      { week: 8, strand: "Algebra", topic: "Cubic Graphs", activities: "Sketching and interpreting" },
      { week: 9, strand: "Algebra", topic: "Rational Expressions", activities: "Simplifying, operations" },
      { week: 10, strand: "Algebra", topic: "Exponential Equations", activities: "Laws of indices, solving" }
    ],
    term2: [
      { week: 1, strand: "Geometry", topic: "Coordinate Geometry Review", activities: "Distance, midpoint, gradient" },
      { week: 2, strand: "Geometry", topic: "Equations of Lines", activities: "Parallel and perpendicular lines" },
      { week: 3, strand: "Geometry", topic: "Triangle Geometry", activities: "Sine and cosine rules review" },
      { week: 4, strand: "Geometry", topic: "Trigonometric Identities", activities: "sin²x + cos²x = 1" },
      { week: 5, strand: "Geometry", topic: "Trig Graph Transformations", activities: "y = A sin(Bx + C)" },
      { week: 6, strand: "Geometry", topic: "Solving Trig Equations", activities: "Multiple solutions 0° to 360°" },
      { week: 7, strand: "Geometry", topic: "Circle Equations", activities: "x² + y² = r², center at origin" },
      { week: 8, strand: "Geometry", topic: "Circles Not at Origin", activities: "(x-a)² + (y-b)² = r²" },
      { week: 9, strand: "Calculus", topic: "Arithmetic Sequences", activities: "Word problems, applications" },
      { week: 10, strand: "Calculus", topic: "Geometric Series", activities: "Sum to infinity, applications" }
    ],
    term3: [
      { week: 1, strand: "Calculus", topic: "Differentiation Review", activities: "First principles, derivative rules" },
      { week: 2, strand: "Calculus", topic: "Tangents and Normals", activities: "Finding equations at a point" },
      { week: 3, strand: "Calculus", topic: "Stationary Points", activities: "Using derivatives for curve sketching" },
      { week: 4, strand: "Calculus", topic: "Introduction to Integration", activities: "Integration as reverse of differentiation" },
      { week: 5, strand: "Calculus", topic: "Definite Integrals", activities: "Evaluating with limits" },
      { week: 6, strand: "Calculus", topic: "Area Under Curves", activities: "Applications of integration" },
      { week: 7, strand: "Statistics", topic: "Sampling Methods", activities: "Random, stratified sampling" },
      { week: 8, strand: "Statistics", topic: "Statistical Analysis", activities: "Standard deviation, interpretation" },
      { week: 9, strand: "Statistics", topic: "Normal Distribution", activities: "Bell curve, z-scores" },
      { week: 10, strand: "Statistics", topic: "Probability Applications", activities: "Compound and conditional" }
    ]
  },
  year13: {
    term1: [
      { week: 1, strand: "Algebra", topic: "Complex Numbers - Introduction", activities: "Imaginary unit i, powers of i, square roots of negatives, parts of complex numbers" },
      { week: 2, strand: "Algebra", topic: "Complex Numbers - Modulus, Argument & Forms", activities: "Modulus, argument, rectangular to polar conversion, Argand diagrams" },
      { week: 3, strand: "Algebra", topic: "Complex Numbers - Operations", activities: "Addition, subtraction, multiplication, division in rectangular form, conjugates" },
      { week: 4, strand: "Algebra", topic: "Complex Numbers - Polar Operations & Equations", activities: "Multiply/divide in polar form, solving equations, sketching lines and rays" },
      { week: 5, strand: "Algebra", topic: "Complex Numbers - Advanced", activities: "Quadratic equations with discriminant < 0, sketching/shading regions, De Moivre's Theorem" },
      { week: 6, strand: "Algebra", topic: "Complex Numbers - Roots & Proofs", activities: "Finding roots of complex numbers, evaluating powers, proving conjugate/modulus/argument formulas" },
      { week: 7, strand: "Algebra", topic: "Binomial Theorem", activities: "Expanding (x+y)^n, finding specific terms, coefficients, term independent of x" },
      { week: 8, strand: "Algebra", topic: "Mathematical Induction", activities: "Proving statements: simple equations, rational equations, equations with n as a power" },
      { week: 9, strand: "Algebra", topic: "Partial Fractions - Basics", activities: "Partial fraction templates, linear and repeated root denominators" },
      { week: 10, strand: "Algebra", topic: "Partial Fractions - Advanced", activities: "Non-linear and irreducible quadratic denominators" },
      { week: 11, strand: "Review", topic: "Term 1 Review & Examination", activities: "Revision of all Algebra strand topics, term test" }
    ],
    term2: [
      { week: 1, strand: "Geometry & Trigonometry", topic: "Vectors - Basics", activities: "Vector addition/subtraction, scalar multiplication, direction, magnitude, unit vectors" },
      { week: 2, strand: "Geometry & Trigonometry", topic: "Vectors - Applications", activities: "Dot product, angle between vectors, parallel/perpendicular proofs, equations of lines" },
      { week: 3, strand: "Geometry & Trigonometry", topic: "Vectors - Line Segments", activities: "Parametric/symmetric forms, division of line segments internally/externally" },
      { week: 4, strand: "Geometry & Trigonometry", topic: "Trigonometric Skills - Basics", activities: "Reciprocal/inverse functions, exact values, sketching trig functions, simple equations" },
      { week: 5, strand: "Geometry & Trigonometry", topic: "Trigonometric Skills - Advanced", activities: "Pythagorean identities, double angle formulas, compound angle formulas, sums and products" },
      { week: 6, strand: "Geometry & Trigonometry", topic: "Trigonometric Functions & Models", activities: "Solving y = A sin B(x+C)+D models, forming equations with amplitude/period/shift" },
      { week: 7, strand: "Geometry & Trigonometry", topic: "Proving Trig Identities", activities: "Proofs using reciprocal, Pythagorean, compound angle, double angle, sum/product formulas" },
      { week: 8, strand: "Differentiation", topic: "Limits and Continuity - Basics", activities: "Piecewise functions, limits algebraically/numerically, discontinuities" },
      { week: 9, strand: "Differentiation", topic: "Limits and Continuity - Advanced", activities: "Limits at infinity, graph features, sketching rational/piecewise graphs, continuity" },
      { week: 10, strand: "Differentiation", topic: "Differentiation Skills", activities: "Sums, quotients, chain rule, parametric functions, second derivatives, first principles" },
      { week: 11, strand: "Review", topic: "Term 2 Review & Examination", activities: "Revision of Geometry, Trigonometry and Limits topics, term test" }
    ],
    term3: [
      { week: 1, strand: "Differentiation", topic: "Differentiation Skills - Maxima & Minima", activities: "Finding maxima/minima with proof, points of inflection" },
      { week: 2, strand: "Differentiation", topic: "Kinematics", activities: "Differentiation for projectile motion, velocity, acceleration, two-derivative problems" },
      { week: 3, strand: "Differentiation", topic: "Optimization Problems", activities: "Maxima/minima with single and double differentiation, overlapping equations" },
      { week: 4, strand: "Differentiation", topic: "Related Rates & Implicit Differentiation", activities: "Chain rule for rates of change, implicit/parametric differentiation, tangent/normal equations" },
      { week: 5, strand: "Differentiation", topic: "Graph Sketching", activities: "Sketching polynomials using derivatives, derived function graphs, concavity analysis" },
      { week: 6, strand: "Integration", topic: "Integration Skills - Basics", activities: "Integrating ax^n, polynomials, exponentials, trig functions, definite integrals" },
      { week: 7, strand: "Integration", topic: "Integration Skills - Advanced", activities: "Rational functions, u-substitution Type I and II, area under curves" },
      { week: 8, strand: "Integration", topic: "Integration Techniques & Kinematics", activities: "Areas for odd/even/periodic functions, numerical methods, anti-differentiation in kinematics" },
      { week: 9, strand: "Integration", topic: "Volumes of Revolution & Differential Equations", activities: "Volumes of revolution, first/second order separable differential equations" },
      { week: 10, strand: "Review", topic: "Comprehensive Review", activities: "Revision of all Year 13 content, practice papers" },
      { week: 11, strand: "Review", topic: "Final Examination Preparation", activities: "Mock examination, exam techniques, final revision" }
    ]
  }
};

// Mathematics Yearly Planner - All Year Levels
export const mathematicsYearlyPlanner = {
  year11: {
    term1: {
      weeks: "Weeks 1-10",
      focus: "Algebra & Number Foundations",
      assessment: "CAT 1 - Algebra Test",
      units: [
        {
          title: "Algebraic Skills",
          weeks: "Weeks 1-3",
          strand: "Algebra",
          subStrand: "1.1",
          topics: ["Simplification", "Factorization", "Expanding"]
        },
        {
          title: "Linear Equations & Inequalities",
          weeks: "Weeks 4-6",
          strand: "Algebra",
          subStrand: "1.2",
          topics: ["Solving equations", "Graphing", "Word problems"]
        },
        {
          title: "Quadratic Functions",
          weeks: "Weeks 7-10",
          strand: "Algebra",
          subStrand: "1.3",
          topics: ["Factoring quadratics", "Completing the square", "Quadratic formula"]
        }
      ]
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "Geometry & Measurement",
      assessment: "TDT 1 - Geometry Project",
      units: [
        {
          title: "Trigonometry Basics",
          weeks: "Weeks 1-4",
          strand: "Geometry",
          subStrand: "2.1",
          topics: ["SOHCAHTOA", "Right triangles", "Angles of elevation/depression"]
        },
        {
          title: "Coordinate Geometry",
          weeks: "Weeks 5-7",
          strand: "Geometry",
          subStrand: "2.2",
          topics: ["Distance formula", "Midpoint", "Gradient"]
        },
        {
          title: "Mensuration",
          weeks: "Weeks 8-10",
          strand: "Measurement",
          subStrand: "3.1",
          topics: ["Surface area", "Volume", "Composite shapes"]
        }
      ]
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "Statistics & Functions",
      assessment: "CAT 2 - Statistics Test",
      units: [
        {
          title: "Data Analysis",
          weeks: "Weeks 1-4",
          strand: "Statistics",
          subStrand: "4.1",
          topics: ["Mean, median, mode", "Frequency tables", "Histograms"]
        },
        {
          title: "Functions & Relations",
          weeks: "Weeks 5-7",
          strand: "Algebra",
          subStrand: "1.4",
          topics: ["Function notation", "Domain and range", "Graphing functions"]
        },
        {
          title: "Exponential & Logarithmic",
          weeks: "Weeks 8-10",
          strand: "Algebra",
          subStrand: "1.5",
          topics: ["Exponential growth", "Log laws", "Applications"]
        }
      ]
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "Review & Examination",
      assessment: "Internal Examination",
      units: [
        {
          title: "Revision - Algebra & Geometry",
          weeks: "Weeks 1-3",
          strand: "All",
          subStrand: "All",
          topics: ["Algebra review", "Geometry review", "Problem solving"]
        },
        {
          title: "Revision - Statistics & Functions",
          weeks: "Weeks 4-6",
          strand: "All",
          subStrand: "All",
          topics: ["Statistics review", "Functions review", "Practice papers"]
        },
        {
          title: "Examination Preparation",
          weeks: "Weeks 7-10",
          strand: "All",
          subStrand: "All",
          topics: ["Mock exams", "Exam techniques", "Final revision"]
        }
      ]
    }
  },
  year12: {
    term1: {
      weeks: "Weeks 1-10",
      focus: "Advanced Algebra & Calculus Introduction",
      assessment: "CAT 1 - Algebra & Calculus",
      units: [
        {
          title: "Advanced Algebraic Manipulation",
          weeks: "Weeks 1-3",
          strand: "Algebra",
          subStrand: "1.1",
          topics: ["Polynomials", "Rational expressions", "Simultaneous equations"]
        },
        {
          title: "Introduction to Calculus",
          weeks: "Weeks 4-7",
          strand: "Calculus",
          subStrand: "5.1",
          topics: ["Limits", "First principles", "Basic differentiation"]
        },
        {
          title: "Differentiation Rules",
          weeks: "Weeks 8-10",
          strand: "Calculus",
          subStrand: "5.2",
          topics: ["Power rule", "Product rule", "Chain rule"]
        }
      ]
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "Trigonometry & Calculus Applications",
      assessment: "TDT 1 - Calculus Project",
      units: [
        {
          title: "Advanced Trigonometry",
          weeks: "Weeks 1-4",
          strand: "Geometry",
          subStrand: "2.1",
          topics: ["Unit circle", "Trig graphs", "Trig identities"]
        },
        {
          title: "Calculus Applications",
          weeks: "Weeks 5-7",
          strand: "Calculus",
          subStrand: "5.3",
          topics: ["Rates of change", "Tangent lines", "Optimization"]
        },
        {
          title: "Introduction to Integration",
          weeks: "Weeks 8-10",
          strand: "Calculus",
          subStrand: "5.4",
          topics: ["Antiderivatives", "Definite integrals", "Area under curves"]
        }
      ]
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "Statistics & Further Calculus",
      assessment: "CAT 2 - Statistics & Integration",
      units: [
        {
          title: "Statistical Analysis",
          weeks: "Weeks 1-4",
          strand: "Statistics",
          subStrand: "4.1",
          topics: ["Standard deviation", "Normal distribution", "Z-scores"]
        },
        {
          title: "Further Integration",
          weeks: "Weeks 5-7",
          strand: "Calculus",
          subStrand: "5.4",
          topics: ["Integration techniques", "Applications", "Volume"]
        },
        {
          title: "Probability",
          weeks: "Weeks 8-10",
          strand: "Statistics",
          subStrand: "4.2",
          topics: ["Compound events", "Conditional probability", "Expected value"]
        }
      ]
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "Review & Examination",
      assessment: "Internal Examination",
      units: [
        {
          title: "Calculus Review",
          weeks: "Weeks 1-3",
          strand: "Calculus",
          subStrand: "All",
          topics: ["Differentiation review", "Integration review", "Applications"]
        },
        {
          title: "Comprehensive Review",
          weeks: "Weeks 4-6",
          strand: "All",
          subStrand: "All",
          topics: ["All topics review", "Practice papers", "Problem solving"]
        },
        {
          title: "Final Preparation",
          weeks: "Weeks 7-10",
          strand: "All",
          subStrand: "All",
          topics: ["Mock exams", "Exam strategies", "Final revision"]
        }
      ]
    }
  },
  year13: {
    term1: {
      weeks: "Weeks 1-11",
      focus: "Algebra: Complex Numbers, Binomial Theorem, Induction & Partial Fractions",
      assessment: "CAT 1 - Algebra (Complex Numbers & Algebraic Techniques)",
      units: [
        {
          title: "Complex Numbers",
          weeks: "Weeks 1-6",
          strand: "Algebra",
          subStrand: "13CAL1.1",
          topics: ["Imaginary numbers", "Modulus & argument", "Rectangular & polar forms", "Argand diagrams", "Operations", "De Moivre's Theorem", "Roots & proofs"]
        },
        {
          title: "Binomial Theorem & Mathematical Induction",
          weeks: "Weeks 7-8",
          strand: "Algebra",
          subStrand: "13CAL1.2-1.3",
          topics: ["Binomial expansion", "Finding terms & coefficients", "Proof by induction"]
        },
        {
          title: "Partial Fractions",
          weeks: "Weeks 9-10",
          strand: "Algebra",
          subStrand: "13CAL1.4",
          topics: ["Linear denominators", "Repeated roots", "Irreducible quadratics"]
        }
      ]
    },
    term2: {
      weeks: "Weeks 1-11",
      focus: "Geometry, Trigonometry & Introduction to Differentiation",
      assessment: "TDT 1 - Geometry & Trigonometry Investigation",
      units: [
        {
          title: "Vectors and Geometry",
          weeks: "Weeks 1-3",
          strand: "Geometry & Trigonometry",
          subStrand: "13CAL2.1",
          topics: ["Vector operations", "Dot product", "Equations of lines", "Line segment division"]
        },
        {
          title: "Trigonometry",
          weeks: "Weeks 4-7",
          strand: "Geometry & Trigonometry",
          subStrand: "13CAL2.2-2.4",
          topics: ["Trig skills & identities", "Double & compound angles", "Trig models", "Proving identities"]
        },
        {
          title: "Limits, Continuity & Differentiation Skills",
          weeks: "Weeks 8-10",
          strand: "Differentiation",
          subStrand: "13CAL3.1-3.2",
          topics: ["Piecewise functions", "Limits & continuity", "Differentiation rules", "First principles"]
        }
      ]
    },
    term3: {
      weeks: "Weeks 1-11",
      focus: "Applications of Differentiation & Integration",
      assessment: "CAT 2 - Differentiation & Integration",
      units: [
        {
          title: "Applications of Differentiation",
          weeks: "Weeks 1-5",
          strand: "Differentiation",
          subStrand: "13CAL3.3-3.4",
          topics: ["Kinematics", "Optimization", "Related rates", "Implicit differentiation", "Graph sketching"]
        },
        {
          title: "Integration",
          weeks: "Weeks 6-9",
          strand: "Integration",
          subStrand: "13CAL4.1-4.4",
          topics: ["Integration skills", "Area techniques", "Volumes of revolution", "Differential equations"]
        },
        {
          title: "Review & Final Examination",
          weeks: "Weeks 10-11",
          strand: "All",
          subStrand: "All",
          topics: ["Comprehensive review", "Practice papers", "Final examination"]
        }
      ]
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "Final Examination Preparation",
      assessment: "Internal & External Examinations",
      units: [
        {
          title: "Comprehensive Review",
          weeks: "Weeks 1-4",
          strand: "All",
          subStrand: "All",
          topics: ["All Year 13 content", "Past papers", "Weak areas focus"]
        },
        {
          title: "Examination Practice",
          weeks: "Weeks 5-7",
          strand: "All",
          subStrand: "All",
          topics: ["Timed practice", "Exam techniques", "Problem solving"]
        },
        {
          title: "Final Preparation",
          weeks: "Weeks 8-10",
          strand: "All",
          subStrand: "All",
          topics: ["Mock exams", "Final revision", "Exam readiness"]
        }
      ]
    }
  }
};

// Mathematics Teaching Resources
export const mathematicsResources = {
  year11: [
    { title: "Algebra Formula Sheet", type: "PDF", description: "Complete formula reference for Year 11 Algebra", unit: "Algebra" },
    { title: "Geometry Theorems Guide", type: "PDF", description: "Summary of all geometry theorems", unit: "Geometry" },
    { title: "Statistics Formulas", type: "PDF", description: "Statistical formulas and methods", unit: "Statistics" },
    { title: "Practice Worksheets Pack", type: "PDF", description: "Additional practice problems for all topics", unit: "All" },
    { title: "Graphing Calculator Guide", type: "PDF", description: "How to use graphing calculators effectively", unit: "All" },
    { title: "Problem Solving Strategies", type: "PDF", description: "Techniques for solving word problems", unit: "All" }
  ],
  year12: [
    { title: "Calculus Formula Sheet", type: "PDF", description: "Differentiation and integration formulas", unit: "Calculus" },
    { title: "Trigonometry Identities", type: "PDF", description: "Complete trig identity reference", unit: "Geometry" },
    { title: "Statistics Tables", type: "PDF", description: "Normal distribution and statistical tables", unit: "Statistics" },
    { title: "Worked Examples Booklet", type: "PDF", description: "Step-by-step solutions to common problems", unit: "All" },
    { title: "Exam Preparation Guide", type: "PDF", description: "Tips and strategies for Year 12 exams", unit: "All" },
    { title: "Past Papers Collection", type: "PDF", description: "Previous year examination papers", unit: "All" }
  ],
  year13: [
    { title: "Complex Numbers Handbook", type: "PDF", description: "Complete guide to complex number operations, Argand diagrams, and De Moivre's Theorem", unit: "Algebra" },
    { title: "Binomial & Induction Guide", type: "PDF", description: "Binomial theorem expansion and mathematical induction proof techniques", unit: "Algebra" },
    { title: "Partial Fractions Reference", type: "PDF", description: "Templates and methods for decomposing rational functions", unit: "Algebra" },
    { title: "Vector Mathematics", type: "PDF", description: "Vector operations, dot product, equations of lines and applications", unit: "Geometry & Trigonometry" },
    { title: "Trigonometric Identities & Proofs", type: "PDF", description: "Complete trig identity reference with double angle, compound angle, and sum/product formulas", unit: "Geometry & Trigonometry" },
    { title: "Advanced Differentiation Guide", type: "PDF", description: "Limits, continuity, differentiation rules, kinematics, optimization, implicit differentiation", unit: "Differentiation" },
    { title: "Integration Techniques", type: "PDF", description: "Integration skills, u-substitution, areas, volumes of revolution, differential equations", unit: "Integration" },
    { title: "Exam Success Guide", type: "PDF", description: "Strategies for Year 13 Mathematics with Calculus final exams", unit: "All" }
  ]
};

export default mathematicsCurriculum;
